import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { PredictionBrainGraph } from "@/components/admin/PredictionBrainGraph";
import {
  useBrainStats,
  useBrainGraph,
  useBrainChunks,
  useBrainChunkDetail,
  useBrainSettings,
  useUpdateBrainSettings,
  useCreateBrainChunk,
  useUpdateBrainChunk,
  useDeleteBrainChunk,
  type BrainSettings,
  type BrainConnection,
} from "@/hooks/useAdminBrain";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Brain,
  GitBranch,
  Network,
  List,
  Activity,
  Plus,
  Search,
  X,
  Pencil,
  Trash2,
  Loader2,
  Filter,
  Settings,
  Zap,
  ChevronDown,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { BRAIN_CATEGORY_COLORS } from "@/components/admin/PredictionBrainGraph";

const BRAIN_CATEGORIES = [
  { key: "calibration_ad", label: "Calibration Ad", color: "#8B5CF6" },
  { key: "rubric_rule", label: "Rubric Rule", color: "#14B8A6" },
  { key: "simulation_pattern", label: "Simulation Pattern", color: "#EC4899" },
  { key: "real_performance", label: "Real Performance", color: "#F87171" },
  { key: "accuracy_signal", label: "Accuracy Signal", color: "#F59E0B" },
  { key: "admin_pin", label: "Admin Pin", color: "#9CA3AF" },
];

type ViewMode = "graph" | "list" | "accuracy";

const VIEWS = [
  { key: "graph" as const, icon: Network, label: "Graph" },
  { key: "list" as const, icon: List, label: "List" },
  { key: "accuracy" as const, icon: Activity, label: "Accuracy" },
];

const TOP_N_OPTIONS = [50, 100, 200, 500, 1000];

// ── Stats Bar ──

function BrainStatsBar() {
  const { data: stats, isLoading } = useBrainStats();

  const cards = [
    { label: "Total Nodes", value: stats?.totalChunks, icon: Brain, color: "#8B5CF6" },
    { label: "Connections", value: stats?.totalConnections, icon: GitBranch, color: "#14B8A6" },
    {
      label: "Active Rubric",
      value: stats?.activeRubric ? stats.activeRubric.name : "—",
      icon: Network,
      color: "#EC4899",
      small: true,
    },
    {
      label: "Accuracy",
      value: stats?.accuracyScore != null ? `${stats.accuracyScore}%` : "—",
      icon: Activity,
      color: "#F59E0B",
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="brain-stats">
      {cards.map((c) => (
        <div key={c.label} className="bg-white border border-[#E5E2DC] rounded-xl px-4 py-3 flex items-center gap-3">
          <div
            className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${c.color}20` }}
          >
            <c.icon className="h-4 w-4" style={{ color: c.color }} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[#746F66]">{c.label}</p>
            {isLoading ? (
              <div className="h-5 w-16 bg-white animate-pulse rounded mt-0.5" />
            ) : (
              <p className={cn("font-bold text-[#1A1A1A] truncate", c.small ? "text-sm mt-0.5" : "text-xl")}>
                {c.value ?? "—"}
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Settings Panel ──

function BrainSettingsPanel({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const { data: settings, isLoading } = useBrainSettings();
  const updateSettings = useUpdateBrainSettings();
  const { toast } = useToast();

  type BooleanSettingsKey = "isEnabled" | "autoIngestEnabled" | "simulationEnabled";

  const handleToggle = async (field: BooleanSettingsKey, value: boolean) => {
    try {
      const update: Partial<BrainSettings> = {};
      update[field] = value;
      await updateSettings.mutateAsync(update);
      toast({ title: "Settings updated" });
    } catch {
      toast({ title: "Failed to update settings", variant: "destructive" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="admin-theme bg-white border-[#E5E2DC] text-[#1A1A1A] sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1A1A1A]">
            <Settings className="h-4 w-4 text-purple-700" />
            Prediction Brain Settings
          </DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-[#746F66]" />
          </div>
        ) : !settings ? (
          <p className="text-sm text-[#746F66] py-4">Settings not available</p>
        ) : (
          <div className="space-y-4 pt-1">
            {[
              { key: "isEnabled", label: "Enable Prediction Brain", desc: "Master toggle for all brain functionality" },
              { key: "autoIngestEnabled", label: "Auto-ingest enabled", desc: "Automatically ingest new data into the brain" },
              { key: "simulationEnabled", label: "Simulation enabled", desc: "Allow rubric simulation runs" },
            ].map(item => (
              <div key={item.key} className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-[#1A1A1A]">{item.label}</p>
                  <p className="text-xs text-[#746F66] mt-0.5">{item.desc}</p>
                </div>
                <Switch
                  checked={!!settings[item.key as keyof typeof settings]}
                  onCheckedChange={v => handleToggle(item.key as BooleanSettingsKey, v)}
                  disabled={updateSettings.isPending}
                  data-testid={`setting-toggle-${item.key}`}
                />
              </div>
            ))}
            <div className="pt-2 border-t border-[#E5E2DC] space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#746F66]">Max chunks total</span>
                <span className="text-sm font-medium text-[#1A1A1A]">{settings.maxChunksTotal?.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#746F66]">Retention days</span>
                <span className="text-sm font-medium text-[#1A1A1A]">{settings.chunkRetentionDays}</span>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Detail Panel ──

function BrainDetailPanel({
  selectedNodeId,
  onClose,
}: {
  selectedNodeId: number | null;
  onClose: () => void;
}) {
  const { data: detail, isLoading } = useBrainChunkDetail(selectedNodeId);
  const updateChunk = useUpdateBrainChunk();
  const deleteChunk = useDeleteBrainChunk();
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editContent, setEditContent] = useState("");

  if (!selectedNodeId) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center p-6">
        <Brain className="h-10 w-10 text-[#746F66] mb-3" />
        <p className="text-sm font-medium text-[#746F66]">Select a node</p>
        <p className="text-xs text-[#746F66] mt-1">Click a node to see details</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#746F66]" />
      </div>
    );
  }

  if (!detail) return null;

  const { chunk } = detail;
  const color = BRAIN_CATEGORY_COLORS[chunk.category] || "#6B7280";
  const catLabel = BRAIN_CATEGORIES.find(c => c.key === chunk.category)?.label ?? chunk.category;

  const handleSave = async () => {
    try {
      await updateChunk.mutateAsync({ id: chunk.id, title: editTitle, content: editContent });
      setIsEditing(false);
      toast({ title: "Chunk updated" });
    } catch {
      toast({ title: "Update failed", variant: "destructive" });
    }
  };

  const handleDelete = async () => {
    try {
      await deleteChunk.mutateAsync(chunk.id);
      onClose();
      toast({ title: "Chunk removed" });
    } catch {
      toast({ title: "Delete failed", variant: "destructive" });
    }
  };

  return (
    <div className="h-full flex flex-col text-[#3D3A36]" data-testid="brain-detail-panel">
      <div className="flex items-center justify-between p-4 border-b border-[#E5E2DC]">
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
          <span className="text-xs font-medium text-[#746F66]">{catLabel}</span>
        </div>
        <div className="flex items-center gap-1">
          {!isEditing && (
            <button
              onClick={() => { setEditTitle(chunk.title); setEditContent(chunk.content); setIsEditing(true); }}
              className="p-1.5 rounded hover:bg-[#F5F3EE] transition-colors"
              data-testid="button-edit-chunk"
            >
              <Pencil className="h-3.5 w-3.5 text-[#746F66]" />
            </button>
          )}
          <button
            onClick={handleDelete}
            className="p-1.5 rounded hover:bg-red-900/40 transition-colors"
            data-testid="button-delete-chunk"
          >
            <Trash2 className="h-3.5 w-3.5 text-red-700" />
          </button>
          <button
            onClick={onClose}
            className="p-1.5 rounded hover:bg-[#F5F3EE] transition-colors"
            data-testid="button-close-panel"
          >
            <X className="h-3.5 w-3.5 text-[#746F66]" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {isEditing ? (
          <div className="space-y-3">
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-[#746F66] mb-1 block">Title</label>
              <Input value={editTitle} onChange={e => setEditTitle(e.target.value)} className="bg-white border-[#E5E2DC] text-[#1A1A1A] text-sm" data-testid="input-edit-title" />
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-[#746F66] mb-1 block">Content</label>
              <Textarea value={editContent} onChange={e => setEditContent(e.target.value)} className="bg-white border-[#E5E2DC] text-[#1A1A1A] text-sm min-h-[160px] resize-none" data-testid="input-edit-content" />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSave} disabled={updateChunk.isPending} className="bg-[#1A1A1A] hover:bg-[#3D3A36]" data-testid="button-save-chunk">
                {updateChunk.isPending && <Loader2 className="h-3 w-3 animate-spin mr-1" />} Save
              </Button>
              <Button size="sm" variant="outline" onClick={() => setIsEditing(false)} className="border-[#E5E2DC] text-[#3D3A36] hover:text-[#1A1A1A] hover:bg-[#F5F3EE]">Cancel</Button>
            </div>
          </div>
        ) : (
          <>
            <h3 className="text-sm font-semibold text-[#1A1A1A] leading-snug">{chunk.title}</h3>
            <p className="text-xs text-[#746F66] leading-relaxed whitespace-pre-wrap">{chunk.content}</p>
            <div className="space-y-2 text-xs">
              {chunk.importance != null && (
                <div className="flex items-center justify-between">
                  <span className="text-[#746F66]">Importance</span>
                  <div className="flex items-center gap-1.5">
                    <div className="h-1.5 w-24 bg-white rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${(chunk.importance / 10) * 100}%`, backgroundColor: color }} />
                    </div>
                    <span className="text-[#3D3A36] font-medium">{chunk.importance}/10</span>
                  </div>
                </div>
              )}
              {chunk.vertical && (
                <div className="flex items-center justify-between">
                  <span className="text-[#746F66]">Vertical</span>
                  <span className="text-[#3D3A36] font-medium">{chunk.vertical}</span>
                </div>
              )}
              {chunk.createdAt && (
                <div className="flex items-center justify-between">
                  <span className="text-[#746F66]">Created</span>
                  <span className="text-[#3D3A36] font-medium">{new Date(chunk.createdAt).toLocaleDateString()}</span>
                </div>
              )}
            </div>
            {detail.connections.length > 0 && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-[#746F66] mb-2">Connections ({detail.connections.length})</p>
                <div className="space-y-1.5">
                  {detail.connections.slice(0, 8).map((conn: BrainConnection, i: number) => {
                    const connColor = BRAIN_CATEGORY_COLORS[conn.chunk?.category ?? ""] || "#6B7280";
                    return (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: connColor }} />
                        <span className="text-[#746F66] flex-1 truncate">{conn.chunk?.title ?? "Unknown"}</span>
                        <span className="text-[#746F66] text-[10px]">{conn.relationship}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── List View ──

function BrainListView({ categories, search }: { categories: string[]; search: string }) {
  const [sort, setSort] = useState("newest");
  const { data, isLoading } = useBrainChunks(1, 200);
  const qc = useQueryClient();
  const deleteChunk = useDeleteBrainChunk();
  const { toast } = useToast();

  const chunks = (data?.chunks ?? []).filter(c => {
    if (categories.length > 0 && !categories.includes(c.category)) return false;
    if (search && !c.title.toLowerCase().includes(search.toLowerCase()) && !c.content.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }).sort((a, b) => {
    if (sort === "newest") return new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime();
    if (sort === "importance") return (b.importance ?? 0) - (a.importance ?? 0);
    if (sort === "category") return (a.category ?? "").localeCompare(b.category ?? "");
    return 0;
  });

  if (isLoading) return <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#746F66]" /></div>;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs text-[#746F66]">{chunks.length} chunks</span>
        <select
          value={sort}
          onChange={e => setSort(e.target.value)}
          className="bg-white border border-[#E5E2DC] text-[#3D3A36] rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-purple-500"
          data-testid="select-list-sort"
        >
          <option value="newest">Newest first</option>
          <option value="importance">By importance</option>
          <option value="category">By category</option>
        </select>
      </div>
      {chunks.length === 0 ? (
        <div className="text-center py-12">
          <Brain className="h-8 w-8 text-slate-700 mx-auto mb-3" />
          <p className="text-sm text-[#746F66]">No chunks match your filters</p>
        </div>
      ) : (
        <div className="space-y-2">
          {chunks.map(chunk => {
            const color = BRAIN_CATEGORY_COLORS[chunk.category] || "#6B7280";
            const catLabel = BRAIN_CATEGORIES.find(c => c.key === chunk.category)?.label ?? chunk.category;
            return (
              <div key={chunk.id} className="bg-white border border-[#E5E2DC] rounded-lg p-3 flex items-start gap-3" data-testid={`chunk-list-item-${chunk.id}`}>
                <span className="w-2 h-2 rounded-full flex-shrink-0 mt-1.5" style={{ backgroundColor: color }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ color, backgroundColor: `${color}20` }}>{catLabel}</span>
                    {chunk.importance != null && <span className="text-[10px] text-[#746F66]">imp: {chunk.importance}</span>}
                    {chunk.vertical && <span className="text-[10px] text-[#746F66] bg-white px-1 py-0.5 rounded">{chunk.vertical}</span>}
                  </div>
                  <p className="text-xs font-medium text-[#3D3A36]">{chunk.title}</p>
                  <p className="text-[11px] text-[#746F66] mt-0.5 line-clamp-2">{chunk.content}</p>
                </div>
                <button
                  onClick={async () => {
                    try {
                      await deleteChunk.mutateAsync(chunk.id);
                      toast({ title: "Chunk removed" });
                    } catch {
                      toast({ title: "Failed to remove chunk", variant: "destructive" });
                    }
                  }}
                  className="p-1 rounded hover:bg-red-900/30 transition-colors flex-shrink-0"
                  data-testid={`button-delete-chunk-list-${chunk.id}`}
                >
                  <Trash2 className="h-3.5 w-3.5 text-red-500/60 hover:text-red-700" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Accuracy Tab (empty state) ──

function AccuracyView() {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <Activity className="h-12 w-12 text-slate-700 mb-4" />
      <p className="text-base font-medium text-[#746F66]">No accuracy data yet</p>
      <p className="text-xs text-[#746F66] mt-1 max-w-sm">
        Accuracy tracking will be available once the rubric has been run against calibration ads. Activate a rubric and run simulations to populate this view.
      </p>
    </div>
  );
}

// ── Add Knowledge Modal ──

function AddKnowledgeModal({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("admin_pin");
  const [vertical, setVertical] = useState("");
  const [importance, setImportance] = useState("5");
  const createChunk = useCreateBrainChunk();
  const { toast } = useToast();

  const handleSave = async () => {
    if (!title.trim() || !content.trim()) return;
    try {
      await createChunk.mutateAsync({
        title: title.trim(),
        content: content.trim(),
        category,
        vertical: vertical.trim() || undefined,
        importance: Number(importance),
      });
      setTitle(""); setContent(""); setCategory("admin_pin"); setVertical(""); setImportance("5");
      onOpenChange(false);
      toast({ title: "Added to Prediction Brain" });
    } catch {
      toast({ title: "Failed to add knowledge", variant: "destructive" });
    }
  };

  const VERTICALS = ["DTC", "SaaS", "Health", "Finance", "Fashion", "Home", "Food", "Travel", "Education", "B2B"];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="admin-theme bg-white border-[#E5E2DC] text-[#1A1A1A] sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1A1A1A]">
            <Brain className="h-4 w-4 text-purple-700" />
            Add Knowledge
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-1">
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-[#746F66] mb-1 block">Category</label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="bg-white border-[#E5E2DC] text-[#1A1A1A]" data-testid="select-chunk-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="admin-theme bg-white border-[#E5E2DC]">
                {BRAIN_CATEGORIES.map(c => (
                  <SelectItem key={c.key} value={c.key} className="text-[#1A1A1A] hover:bg-[#F5F3EE]">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: c.color }} />
                      {c.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-[#746F66] mb-1 block">Title</label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g., Hook pattern for DTC health brands" className="bg-white border-[#E5E2DC] text-[#1A1A1A] placeholder:text-[#746F66]" data-testid="input-chunk-title" />
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-[#746F66] mb-1 block">Content</label>
            <Textarea value={content} onChange={e => setContent(e.target.value)} placeholder="Describe the pattern, insight, or signal..." className="bg-white border-[#E5E2DC] text-[#1A1A1A] placeholder:text-[#746F66] min-h-[90px]" data-testid="input-chunk-content" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-[#746F66] mb-1 block">Vertical</label>
              <Select value={vertical || "__none__"} onValueChange={v => setVertical(v === "__none__" ? "" : v)}>
                <SelectTrigger className="bg-white border-[#E5E2DC] text-[#1A1A1A] text-sm" data-testid="select-chunk-vertical">
                  <SelectValue placeholder="Any" />
                </SelectTrigger>
                <SelectContent className="admin-theme bg-white border-[#E5E2DC]">
                  <SelectItem value="__none__" className="text-[#746F66]">Any vertical</SelectItem>
                  {VERTICALS.map(v => (
                    <SelectItem key={v} value={v} className="text-[#1A1A1A] hover:bg-[#F5F3EE]">{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-[#746F66] mb-1 block">Importance</label>
              <Input type="number" min="1" max="10" value={importance} onChange={e => setImportance(e.target.value)} className="bg-white border-[#E5E2DC] text-[#1A1A1A] text-sm" data-testid="input-chunk-importance" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} className="border-[#E5E2DC] text-[#3D3A36] hover:text-[#1A1A1A] hover:bg-[#F5F3EE]" data-testid="button-cancel-knowledge">Cancel</Button>
            <Button size="sm" onClick={handleSave} disabled={!title.trim() || !content.trim() || createChunk.isPending} className="bg-[#1A1A1A] hover:bg-[#3D3A36]" data-testid="button-save-knowledge">
              {createChunk.isPending && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
              Add Knowledge
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Main Page ──

export default function PredictionBrainPage() {
  const [selectedNodeId, setSelectedNodeId] = useState<number | null>(null);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<ViewMode>("graph");
  const [addOpen, setAddOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [topN, setTopN] = useState(200);

  const { data: graphData, isLoading: graphLoading } = useBrainGraph(selectedCategories, searchTerm);
  const { toast } = useToast();

  const displayedNodes = (graphData?.nodes ?? []).slice(0, topN);
  const nodeIds = new Set(displayedNodes.map(n => n.id));
  const displayedEdges = (graphData?.edges ?? []).filter(e => {
    const resolveId = (endpoint: number | { id: number }): number =>
      typeof endpoint === "object" && endpoint !== null ? endpoint.id : (endpoint as number);
    const src = resolveId(e.source as number | { id: number });
    const tgt = resolveId(e.target as number | { id: number });
    return nodeIds.has(src) && nodeIds.has(tgt);
  });

  const toggleCategory = (key: string) => {
    setSelectedCategories(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const handleScanIntelligence = () => {
    toast({
      title: "Scan Intelligence",
      description: "Platform-wide intelligence scan pipeline is not yet wired. Check back in Phase 2.",
    });
  };

  return (
    <>
    <div className="flex flex-col gap-5 min-h-[calc(100vh-4rem)] lg:h-[calc(100vh-4.5rem)]">
        <div className="flex-shrink-0">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-[#1A1A1A] flex items-center gap-2.5" data-testid="text-brain-title">
                Prediction brain
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-purple-500" />
                </span>
              </h1>
              <p className="text-sm text-[#746F66] mt-0.5">Ad Intelligence System</p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={handleScanIntelligence}
                className="border-[#E5E2DC] text-[#3D3A36] hover:text-[#1A1A1A] hover:bg-[#F5F3EE]"
                data-testid="button-scan-intelligence"
              >
                <Zap className="h-3.5 w-3.5 mr-1.5 text-purple-700" />
                Scan intelligence
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setSettingsOpen(true)}
                className="border-[#E5E2DC] text-[#3D3A36] hover:text-[#1A1A1A] hover:bg-[#F5F3EE]"
                data-testid="button-brain-settings"
              >
                <Settings className="h-3.5 w-3.5" />
              </Button>
              <Button
                onClick={() => setAddOpen(true)}
                className="bg-[#1A1A1A] hover:bg-[#3D3A36] text-white"
                data-testid="button-add-knowledge"
              >
                <Plus className="h-4 w-4 mr-1.5" />
                Add knowledge
              </Button>
            </div>
          </div>

          <BrainStatsBar />
        </div>

        <div className="flex-shrink-0 flex items-center gap-3 flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#746F66]" />
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search nodes..."
              className="pl-8 pr-3 py-1.5 text-sm bg-white border border-[#E5E2DC] rounded-lg text-[#1A1A1A] placeholder:text-[#746F66] focus:outline-none focus:ring-1 focus:ring-purple-500 w-48"
              data-testid="input-brain-search"
            />
          </div>

          <div className="flex items-center gap-1.5 flex-wrap">
            <Filter className="h-3.5 w-3.5 text-[#746F66] flex-shrink-0" />
            <button
              onClick={() => setSelectedCategories([])}
              className={cn("px-2.5 py-1 rounded-full text-xs font-medium border transition-colors", selectedCategories.length === 0 ? "bg-[#F0EDE7] text-[#1A1A1A] border-[#E5E2DC]" : "border-[#E5E2DC] text-[#746F66] hover:text-[#3D3A36] hover:border-[#E5E2DC]")}
              data-testid="filter-chip-all"
            >All</button>
            {BRAIN_CATEGORIES.map(cat => {
              const active = selectedCategories.includes(cat.key);
              return (
                <button
                  key={cat.key}
                  onClick={() => toggleCategory(cat.key)}
                  className={cn("px-2.5 py-1 rounded-full text-xs font-medium border transition-colors flex items-center gap-1.5", active ? "text-[#1A1A1A] border-transparent" : "border-[#E5E2DC] text-[#746F66] hover:text-[#3D3A36] hover:border-[#E5E2DC]")}
                  style={active ? { backgroundColor: cat.color, borderColor: cat.color } : {}}
                  data-testid={`filter-chip-${cat.key}`}
                >
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: active ? "white" : cat.color }} />
                  {cat.label}
                </button>
              );
            })}
          </div>

          <div className="ml-auto flex flex-wrap items-center gap-2">
            {viewMode === "graph" && (
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-[#746F66]">Show top</span>
                <select
                  value={topN}
                  onChange={e => setTopN(Number(e.target.value))}
                  className="bg-white border border-[#E5E2DC] text-[#3D3A36] rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-purple-500"
                  data-testid="select-top-n"
                >
                  {TOP_N_OPTIONS.map(n => (
                    <option key={n} value={n}>{n} nodes</option>
                  ))}
                </select>
              </div>
            )}
            <div className="flex items-center bg-white border border-[#E5E2DC] rounded-lg p-0.5">
              {VIEWS.map(v => (
                <button
                  key={v.key}
                  onClick={() => setViewMode(v.key)}
                  className={cn("flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors", viewMode === v.key ? "bg-[#1A1A1A]/30 text-purple-700" : "text-[#746F66] hover:text-[#3D3A36]")}
                  data-testid={`view-mode-${v.key}`}
                >
                  <v.icon className="h-3.5 w-3.5" />
                  {v.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex-1 min-h-0 flex flex-col lg:flex-row gap-4">
          <div className={cn("min-w-0 h-[400px] min-h-[400px] lg:h-auto", viewMode === "graph" ? "flex-1 bg-white rounded-xl border border-[#E5E2DC] overflow-hidden relative" : "flex-1 bg-white rounded-xl border border-[#E5E2DC] overflow-y-auto p-4")}>
            {viewMode === "graph" && (
              <>
                {graphLoading && (
                  <div className="absolute inset-0 flex items-center justify-center z-10 bg-white">
                    <Loader2 className="h-6 w-6 animate-spin text-purple-700" />
                  </div>
                )}
                <PredictionBrainGraph
                  nodes={displayedNodes}
                  edges={displayedEdges}
                  selectedNodeId={selectedNodeId}
                  onSelectNode={setSelectedNodeId}
                  searchTerm={searchTerm}
                />
              </>
            )}
            {viewMode === "list" && (
              <BrainListView categories={selectedCategories} search={searchTerm} />
            )}
            {viewMode === "accuracy" && <AccuracyView />}
          </div>

          {viewMode === "graph" && (
            <div className="w-full lg:w-72 flex-shrink-0 bg-white border border-[#E5E2DC] rounded-xl overflow-hidden">
              <BrainDetailPanel
                selectedNodeId={selectedNodeId}
                onClose={() => setSelectedNodeId(null)}
              />
            </div>
          )}
        </div>
      </div>

      <AddKnowledgeModal open={addOpen} onOpenChange={setAddOpen} />
      <BrainSettingsPanel open={settingsOpen} onOpenChange={setSettingsOpen} />
    </>
  );
}
